import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { ArrowRight, Star, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";

// ── Scroll-reveal hook ────────────────────────────────────────────────────────
// deps: re-run whenever async content finishes loading, so newly-added
// [data-reveal] elements get observed even if they weren't in the DOM on mount.
function useReveal(deps = []) {
  useEffect(() => {
    const els = document.querySelectorAll("[data-reveal]:not(.in-view)");
    if (!els.length) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in-view");
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export default function Home() {
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const { settings } = useSiteSettings();

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => setServices([]));
    api.get("/reviews/public?limit=24").then((r) => setReviews(r.data || [])).catch(() => setReviews([]));
  }, []);

  useReveal([services, reviews]);

  const featured = services.filter((s) => s.slug !== "consultation").slice(0, 6);
  const heroImg = settings?.hero_image || "/hero.jpg";
  const heroEyebrow = settings?.hero_eyebrow || "New Jersey · Studio Photography";
  const heroLine1 = settings?.hero_title_line1 || "Light, shape, presence.";
  const heroLine2 = settings?.hero_title_line2 || "Captured with intent.";
  const heroDesc =
    settings?.hero_description ||
    "Allure Image is an editorial photography studio crafting refined portraits, brand stories, and life's milestones.";
  const aboutEyebrow = settings?.about_eyebrow || "The Studio";
  const aboutTitle =
    settings?.about_title ||
    "A modern photography studio for portraits, brands, and the moments in between.";
  const aboutBody =
    settings?.about_body ||
    "From maternity and family portraits to glamour, graduation, and brand campaigns — every session is designed around you. Thoughtful direction, considered lighting, and a finished gallery you'll return to for years.";

  return (
    <div>
      {/* ── HERO ───────────────────────────────────────────────────────── */}
      <section
        className="relative min-h-[78vh] md:min-h-[88vh] flex items-end overflow-hidden"
        data-testid="hero-section"
      >
        <img
          src={heroImg}
          alt="Allure Image hero"
          className="absolute inset-0 w-full h-full object-cover object-[center_30%]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/75" />

        <div className="container-x relative z-10 pb-16 md:pb-32 text-white">
          <div className="text-xs uppercase font-medium tracking-[0.24em] text-white/75 mb-4 md:mb-6">
            {heroEyebrow}
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight font-light max-w-4xl">
            {heroLine1}
            <br /> <AccentLastWord text={heroLine2} />
          </h1>
          <p className="mt-6 md:mt-8 max-w-xl text-white/80 text-sm sm:text-base md:text-lg leading-relaxed">
            {heroDesc}
          </p>
          <div className="mt-8 md:mt-12 flex flex-wrap items-center gap-3">
            <Link
              to="/booking"
              className="inline-flex items-center gap-3 px-6 sm:px-7 h-12 bg-white text-stone-900 text-xs uppercase tracking-[0.22em] hover:bg-white/92 transition-colors group"
              data-testid="hero-book-btn"
            >
              Book a Session
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center gap-3 px-6 sm:px-7 h-12 border border-white/50 text-white text-xs uppercase tracking-[0.22em] hover:bg-white/10 transition-colors"
              data-testid="hero-view-services-btn"
            >
              View Packages
            </Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-7 right-8 md:right-14 flex flex-col items-center gap-2 text-white/55 z-10 hidden md:flex">
          <span className="text-[9px] uppercase tracking-[0.3em] rotate-90 origin-center translate-x-5">
            Scroll
          </span>
          <div className="bounce-gentle">
            <ChevronDown className="w-4 h-4" strokeWidth={1.5} />
          </div>
        </div>
      </section>

      {/* ── THIN GOLD DIVIDER ─────────────────────────────────────────── */}
      <div className="h-px bg-gradient-to-r from-transparent via-[hsl(var(--gold))] to-transparent" />

      {/* ── INTRO ─────────────────────────────────────────────────────── */}
      <section className="container-x py-16 md:py-32 grid md:grid-cols-12 gap-6 md:gap-12">
        <div className="md:col-span-4" data-reveal>
          <div className="eyebrow mb-4">{aboutEyebrow}</div>
          <span className="gold-rule" />
        </div>
        <div className="md:col-span-8" data-reveal data-delay="2">
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight font-light">
            {aboutTitle}
          </h2>
          <p className="mt-6 md:mt-8 text-base md:text-lg text-foreground/65 max-w-2xl leading-relaxed">
            {aboutBody}
          </p>
          {/* Micro stats */}
          <div className="mt-10 md:mt-14 grid grid-cols-3 gap-6 pt-8 border-t border-border">
            {[
              { num: "7+", label: "Packages" },
              { num: "200+", label: "Happy clients" },
              { num: "5★", label: "Average rating" },
            ].map((s, i) => (
              <div key={s.label} data-reveal data-delay={`${i + 3}`}>
                <div className="font-serif text-3xl text-foreground">{s.num}</div>
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mt-1.5">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PACKAGES ──────────────────────────────────────────────────── */}
      <section className="bg-card border-y border-border" data-testid="featured-packages">
        <div className="container-x py-16 md:py-32">
          <div className="flex items-end justify-between mb-10 md:mb-16" data-reveal>
            <div>
              <div className="eyebrow mb-3">Signature Sessions</div>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light">
                Photoshoot packages
              </h2>
            </div>
            <Link
              to="/services"
              className="hidden md:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground transition-colors group"
              data-testid="see-all-services"
            >
              See all
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10 md:gap-x-8 md:gap-y-16">
            {featured.map((s, i) => (
              <Link
                key={s.id}
                to={`/booking?service=${s.slug}`}
                className="group"
                data-testid={`featured-${s.slug}`}
                data-reveal
                data-delay={`${(i % 3) + 1}`}
              >
                <div className="aspect-[4/5] overflow-hidden bg-muted">
                  <img
                    src={s.image_url}
                    alt={s.name}
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-106"
                  />
                </div>
                <div className="mt-5">
                  <div className="flex items-baseline justify-between">
                    <h3 className="font-serif text-2xl group-hover:text-[hsl(var(--gold))] transition-colors duration-300">
                      {s.name}
                    </h3>
                    <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                      from ${Math.min(...s.tiers.map((t) => t.price)).toFixed(0)}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{s.description}</p>
                  <div className="mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground group-hover:text-foreground transition-colors">
                    Book now
                    <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROCESS ───────────────────────────────────────────────────── */}
      <section className="container-x py-16 md:py-32 grid md:grid-cols-12 gap-8 md:gap-12">
        <div className="md:col-span-4" data-reveal>
          <div className="eyebrow mb-4">How it works</div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light leading-tight">
            A simple, considered <span className="accent-italic">process.</span>
          </h2>
          <p className="mt-5 text-sm text-muted-foreground leading-relaxed max-w-xs">
            From first inquiry to delivered gallery — clear steps, zero guesswork.
          </p>
        </div>

        <div className="md:col-span-8">
          <div className="grid sm:grid-cols-3 gap-8 md:gap-10">
            {[
              {
                step: "01",
                title: "Choose your package",
                body: "Browse signature sessions and select the tier that fits your vision and budget.",
              },
              {
                step: "02",
                title: "Reserve your date",
                body: "Pick your perfect date and time. Secure your slot with a small Zelle deposit.",
              },
              {
                step: "03",
                title: "Step in front of the lens",
                body: "Arrive, relax, and let us craft images you'll treasure for a lifetime.",
              },
            ].map((p, i) => (
              <div key={p.step} data-reveal data-delay={`${i + 1}`}>
                {/* Number badge */}
                <div className="w-12 h-12 border border-[hsl(var(--gold))]/50 flex items-center justify-center mb-6 group-hover:bg-[hsl(var(--gold))]/10">
                  <span className="font-serif text-xl text-[hsl(var(--gold))]">{p.step}</span>
                </div>
                <h4 className="text-base font-medium">{p.title}</h4>
                <p className="mt-2.5 text-sm text-muted-foreground leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>

          {/* Connector line */}
          <div className="hidden sm:flex items-center gap-4 mt-10 md:mt-14 pt-10 border-t border-border">
            <div className="w-full h-px bg-gradient-to-r from-[hsl(var(--gold))]/60 via-[hsl(var(--gold))]/30 to-transparent" />
            <Link
              to="/booking"
              className="shrink-0 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground transition-colors group"
            >
              Start now
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ──────────────────────────────────────────────── */}
      {reviews.length > 0 && <Testimonials reviews={reviews} />}

      {/* ── CTA ───────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        {/* Dot grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "radial-gradient(circle, hsl(var(--gold)) 1px, transparent 1px)",
            backgroundSize: "36px 36px",
          }}
        />
        {/* Gradient fade at left */}
        <div className="absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-primary to-transparent" />

        <div className="container-x py-20 md:py-36 relative z-10 grid md:grid-cols-12 gap-8 md:gap-12 items-end">
          <div className="md:col-span-8" data-reveal>
            <div className="text-[10px] uppercase tracking-[0.3em] text-primary-foreground/40 mb-5">
              Ready when you are
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl md:text-7xl font-light leading-[0.95] tracking-tight">
              Let&apos;s create something{" "}
              <span className="italic text-[hsl(var(--gold))]">timeless</span>.
            </h2>
          </div>
          <div className="md:col-span-4 flex md:justify-end" data-reveal data-delay="2">
            <div className="flex flex-col items-start gap-4">
              <Link
                to="/booking"
                className="inline-flex items-center gap-3 px-7 h-12 bg-background text-foreground text-xs uppercase tracking-[0.22em] hover:bg-background/92 transition-colors group"
                data-testid="cta-book-btn"
              >
                Book a Session
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary-foreground/50 hover:text-primary-foreground transition-colors group"
              >
                Or send us a message
                <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function AccentLastWord({ text }) {
  if (!text || !text.trim().length) return null;
  const trimmed = text.trim();
  const lastSpace = trimmed.lastIndexOf(" ");
  if (lastSpace === -1) return <span className="accent-italic">{trimmed}</span>;
  const head = trimmed.slice(0, lastSpace);
  const last = trimmed.slice(lastSpace + 1);
  return (
    <>
      {head} <span className="accent-italic">{last}</span>
    </>
  );
}

function Testimonials({ reviews }) {
  const featured = reviews.filter((r) => r.is_featured);
  const carouselReviews = (featured.length >= 3 ? featured : reviews).slice(0, 6);
  const gridReviews = reviews.slice(0, 9);
  const avg = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : null;
  const recommendCount = reviews.filter((r) => r.would_recommend).length;
  const recommendPct =
    reviews.length ? Math.round((recommendCount / reviews.length) * 100) : null;

  return (
    <section className="bg-accent/30" data-testid="home-testimonials" id="testimonials">
      <div className="container-x py-16 md:py-28">
        <div className="grid md:grid-cols-12 gap-6 md:gap-10 mb-10 md:mb-16 items-end">
          <div className="md:col-span-7" data-reveal>
            <div className="eyebrow mb-4">Loved by our clients</div>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light leading-tight">
              &ldquo;It felt like art,{" "}
              <span className="accent-italic">not a photoshoot.&rdquo;</span>
            </h2>
          </div>
          <div className="md:col-span-5 flex flex-col sm:flex-row md:justify-end gap-6 sm:gap-10" data-reveal data-delay="2">
            {avg && (
              <div>
                <div className="eyebrow text-muted-foreground mb-1.5">Average rating</div>
                <div className="flex items-center gap-2">
                  <span className="font-serif text-3xl">{avg}</span>
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star
                        key={n}
                        className={`w-4 h-4 ${n <= Math.round(avg) ? "fill-[hsl(var(--gold))] text-[hsl(var(--gold))]" : "text-muted-foreground/30"}`}
                        strokeWidth={1.5}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
            {recommendPct !== null && reviews.length >= 3 && (
              <div>
                <div className="eyebrow text-muted-foreground mb-1.5">Would recommend</div>
                <div className="font-serif text-3xl">{recommendPct}%</div>
              </div>
            )}
          </div>
        </div>

        <div data-reveal data-delay="1">
          <TestimonialCarousel reviews={carouselReviews} />
        </div>

        {gridReviews.length > 3 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
            {gridReviews.map((r, i) => (
              <div key={r.id} data-reveal data-delay={`${(i % 3) + 1}`}>
                <TestimonialCard r={r} compact />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function TestimonialCarousel({ reviews }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (reviews.length <= 1) return;
    const t = setTimeout(() => setIdx((i) => (i + 1) % reviews.length), 7000);
    return () => clearTimeout(t);
  }, [idx, reviews.length]);

  if (!reviews.length) return null;
  const r = reviews[idx];

  return (
    <div className="relative" data-testid="testimonial-carousel">
      <div className="bg-card border border-border p-7 sm:p-12 min-h-[260px]">
        <div className="flex items-center gap-1 mb-5">
          {[1, 2, 3, 4, 5].map((n) => (
            <Star
              key={n}
              className={`w-5 h-5 ${n <= r.rating ? "fill-[hsl(var(--gold))] text-[hsl(var(--gold))]" : "text-muted-foreground/30"}`}
              strokeWidth={1.5}
            />
          ))}
        </div>
        <blockquote className="font-serif text-xl md:text-2xl lg:text-3xl font-light leading-snug border-l-2 border-[hsl(var(--gold))] pl-5 md:pl-7">
          &ldquo;{r.comment || "An incredible experience from start to finish."}&rdquo;
        </blockquote>
        <div className="mt-6 flex items-center gap-4">
          {r.photo_url && (
            <img
              src={`${BACKEND}${r.photo_url}?w=200`}
              alt={r.display_name}
              className="w-14 h-14 rounded-full object-cover"
            />
          )}
          <div>
            <div className="font-medium">{r.display_name}</div>
            {r.service_name && (
              <div className="text-xs text-muted-foreground mt-0.5">{r.service_name}</div>
            )}
          </div>
        </div>
      </div>

      {reviews.length > 1 && (
        <>
          <button
            onClick={() => setIdx((i) => (i - 1 + reviews.length) % reviews.length)}
            className="absolute -left-2 sm:-left-4 top-1/2 -translate-y-1/2 w-10 h-10 inline-flex items-center justify-center bg-background border border-border hover:border-foreground/50 transition-colors"
            aria-label="Previous"
            data-testid="testimonial-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => setIdx((i) => (i + 1) % reviews.length)}
            className="absolute -right-2 sm:-right-4 top-1/2 -translate-y-1/2 w-10 h-10 inline-flex items-center justify-center bg-background border border-border hover:border-foreground/50 transition-colors"
            aria-label="Next"
            data-testid="testimonial-next"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <div className="flex justify-center gap-1.5 mt-5">
            {reviews.map((rv, i) => (
              <button
                key={rv.id}
                onClick={() => setIdx(i)}
                className={`h-[2px] transition-all duration-300 ${
                  i === idx
                    ? "w-8 bg-[hsl(var(--gold))]"
                    : "w-4 bg-foreground/20 hover:bg-foreground/50"
                }`}
                aria-label={`Show testimonial ${i + 1}`}
                data-testid={`testimonial-dot-${i}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function TestimonialCard({ r, compact }) {
  return (
    <div
      className="bg-card border border-border p-5 hover:border-border/80 transition-colors"
      data-testid={`testimonial-card-${r.id}`}
    >
      <div className="flex items-center gap-0.5 mb-3">
        {[1, 2, 3, 4, 5].map((n) => (
          <Star
            key={n}
            className={`w-3.5 h-3.5 ${n <= r.rating ? "fill-[hsl(var(--gold))] text-[hsl(var(--gold))]" : "text-muted-foreground/30"}`}
            strokeWidth={1.5}
          />
        ))}
      </div>
      <p className={`text-sm leading-relaxed text-foreground/75 ${compact ? "line-clamp-4" : ""}`}>
        &ldquo;{r.comment}&rdquo;
      </p>
      <div className="mt-4 flex items-center gap-3">
        {r.photo_url && (
          <img
            src={`${BACKEND}${r.photo_url}?w=120`}
            alt={r.display_name}
            className="w-9 h-9 rounded-full object-cover"
          />
        )}
        <div>
          <div className="text-sm font-medium">{r.display_name}</div>
          {r.service_name && (
            <div className="text-xs text-muted-foreground">{r.service_name}</div>
          )}
        </div>
      </div>
    </div>
  );
}
